import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, PartyPopper, HeartHandshake, ArrowRight, Check, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LandingHeader } from './landing/LandingHeader';
import { LandingFooter } from './landing/LandingFooter';
// import { SocialPostCard } from './landing/SocialPostCard'; // 2026-02-25 暫時隱藏 Community
import { FeatureCard } from './landing/FeatureCard';
import { HowItWorksStep } from './landing/HowItWorksStep';
import { TestimonialStoryCard } from './landing/TestimonialStoryCard';
import { HeroConfetti } from './landing/HeroConfetti';
import { HeroPhoneShowcase } from './landing/HeroPhoneShowcase';
import { TemplateGallery } from './landing/TemplateGallery';
import { features, steps, testimonials } from './landing/data';
import { BRAND_CONFIG } from '@/config/brand';



const LandingPage: React.FC<{ onCTAClick: () => void }> = ({ onCTAClick }) => {

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-wedding-bg via-pink-50 to-white text-wedding-text-body">
      {/* Header */}
      <LandingHeader
        variant="A"
        brandName={BRAND_CONFIG.name}
        navItems={[
          { label: '邀請函模板', href: '#templates' },
          { label: '功能亮點', href: '#features' },
          { label: '如何運作', href: '#how-it-works' },
          { label: '新人見證', href: '#testimonials' },
          { label: '婚禮靈感', href: '/blog' },
        ]}
        rightSlot={(
          <>
            <Button variant="ghost" asChild className="px-2 md:px-4">
              <Link to="/login" className="text-wedding-text-body hover:text-wedding-pink">登入</Link>
            </Button>
            <Button asChild className="bg-wedding-pink hover:bg-wedding-pink/90 text-white rounded-full px-3 md:px-5 shadow-lg shadow-wedding-pink/30 transform hover:scale-105 transition-transform">
              <Link to="/register">
                免費建立邀請函 <Sparkles className="ml-2 h-4 w-4 hidden md:inline" />
              </Link>
            </Button>
          </>
        )}
      />

      {/* Main Content */}
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative bg-cover bg-center min-h-[85vh] flex items-center bg-pink-50">
          <HeroConfetti />
          <div className="container mx-auto px-4 md:px-6 py-16 md:py-20">
            <div className="grid items-center gap-10 lg:gap-12 lg:grid-cols-[3fr_2fr]">
              {/* Left: copy + CTA + stats */}
              <div className="text-center lg:text-left">
                <div className="inline-block rounded-full bg-wedding-pink/20 backdrop-blur-sm px-4 py-1.5 text-sm font-semibold tracking-wider mb-6 text-wedding-text-title">
                  告別繁瑣 化繁為簡
                </div>

                <h1 className="text-4xl md:text-6xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 via-pink-500 to-rose-500">你的夢想婚禮</span><br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500">從此輕鬆實現</span>
                </h1>

                <p className="text-xl md:text-2xl mb-8 text-wedding-text-body max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                  為一生一次的大事，省下數十小時的煩惱
                </p>

                <div className="flex flex-col sm:flex-row justify-center lg:justify-start items-center gap-4">
                  <Button size="lg" asChild className="w-full sm:w-auto bg-wedding-pink hover:bg-wedding-pink/90 text-white rounded-full px-10 py-6 text-lg font-bold shadow-2xl shadow-wedding-pink/40 transform hover:scale-105 transition-all duration-300" onClick={onCTAClick}>
                    <Link to="/register">
                      免費建立邀請函 <PartyPopper className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild className="w-full sm:w-auto bg-white/70 backdrop-blur-sm border-2 border-wedding-pink/50 text-wedding-pink hover:bg-wedding-pink/10 hover:text-wedding-pink rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300">
                    <a href="/invite/demo101wedding" target="_blank" rel="noopener noreferrer">
                      先看看範例邀請函 <Eye className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                </div>

                {/* Phone showcase — mobile only, between CTA and stats */}
                <div className="lg:hidden mt-12">
                  <HeroPhoneShowcase className="w-[220px]" />
                </div>

                {/* Hero Stats */}
                <div className="mt-12 flex justify-center lg:justify-start items-center gap-8 text-gray-600">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-700">100+</div>
                    <div className="text-sm">幸福新人</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-700">5 分鐘</div>
                    <div className="text-sm">完成設定</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-700">好幾個</div>
                    <div className="text-sm">沒日沒夜省下來</div>
                  </div>
                </div>
              </div>

              {/* Right: phone showcase — desktop only */}
              <div className="hidden lg:flex justify-center">
                <HeroPhoneShowcase className="w-[300px]" />
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 inset-x-0 flex justify-center text-gray-600 animate-bounce">
            <div className="flex flex-col items-center">
              <span className="text-sm mb-2">向下滾動探索更多</span>
              <ArrowRight className="h-6 w-6 transform rotate-90" />
            </div>
          </div>
        </section>

        {/* Template Gallery Section */}
        <TemplateGallery />

        {/* Features Section */}
        <section id="features" className="w-full py-20 md:py-24 bg-gradient-to-br from-white via-pink-50 to-rose-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="mx-auto mb-16 max-w-3xl text-center">
                <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-wedding-pink to-pink-400">少煩惱，多期待</h2>
                <p className="mt-4 text-lg text-wedding-text-body">每一項功能都為您的完美婚禮而設計，讓規劃過程輕鬆愉快</p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {features.map((f, idx) => (
                <FeatureCard
                  key={idx}
                  mode="image"
                  title={f.title}
                  desc={f.desc}
                  imageSrc={f.imageSrc}
                  alt={f.alt}
                  gradientClass={f.gradientClass}
                  iconName={f.iconName}
                  badges={f.badges}
                />
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="w-full py-20 md:py-24 bg-pink-50/50">
            <div className="container mx-auto px-4 md:px-6">
                <div className="mx-auto mb-16 max-w-3xl text-center">
                    <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-wedding-pink to-pink-400">簡單三步驟，一切盡在掌握！</h2>
                    <p className="mt-4 text-lg text-wedding-text-body">三步驟，從一片混亂到一切就緒</p>
                </div>
                <div className="grid gap-16">
                  {steps.map((s, idx) => (
                    <HowItWorksStep
                      key={idx}
                      imageSrc={s.imageSrc}
                      alt={s.alt}
                      title={s.title}
                      text={s.text}
                      reverse={idx === 1}
                    />
                  ))}
                </div>
            </div>
        </section>

        {/* Testimonials Section — Horizontal Scroll */}
        <section id="testimonials" className="w-full py-20 md:py-24 bg-gradient-to-br from-rose-50 via-pink-50 to-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="mx-auto mb-16 max-w-3xl text-center">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-wedding-pink to-pink-400">真實故事，溫馨見證</h2>
              <p className="mt-4 text-lg text-wedding-text-body">每一對新人的幸福時刻，都是我們最大的動力</p>
            </div>

            <div className="overflow-hidden -mx-4 px-4 pb-4">
              <div className="flex gap-6 animate-marquee w-max">
                {[...testimonials, ...testimonials].map((t, idx) => (
                  <div key={idx} className="shrink-0 w-[320px] md:w-[360px]">
                    <TestimonialStoryCard {...t} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 2026-02-25 暫時隱藏 Community Section
        <section className="w-full py-20 md:py-24 bg-gradient-to-br from-pink-50 via-rose-50 to-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="mx-auto mb-16 max-w-3xl text-center">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-wedding-pink to-pink-400">來自我們社群的真實聲音</h2>
                <p className="mt-4 text-lg text-wedding-text-body">和其他新人一起分享籌備路上的每一刻</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
              {socialPosts.map((p, idx) => (
                <SocialPostCard key={idx} {...p} />
              ))}
            </div>
          </div>
        </section>
        */}

        {/* Final CTA Section */}
        <section className="w-full bg-gradient-to-br from-pink-50 via-rose-50 to-amber-50 relative overflow-hidden">
          <HeroConfetti />

          <div className="container mx-auto px-4 md:px-6 py-24 md:py-32 relative z-10">
            <div className="mx-auto max-w-5xl text-center">
              <div className="mb-8">
                <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 via-pink-500 to-rose-500">準備好開始您的</span>
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500">
                    完美婚禮之旅了嗎？
                  </span>
                </h2>
                <p className="text-xl md:text-2xl text-wedding-text-body max-w-3xl mx-auto leading-relaxed">
                  不用再熬夜做 Excel、不用再跟另一半為了規劃而吵架
                </p>
              </div>

              <div className="flex flex-wrap justify-center items-center gap-8 mb-12">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-wedding-pink" />
                  <span className="text-gray-600">安全加密</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-wedding-pink" />
                  <span className="text-gray-600">隨時取消</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-wedding-pink" />
                  <span className="text-gray-600">免費版不用綁卡</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button size="lg" asChild className="w-full sm:w-auto bg-wedding-pink hover:bg-wedding-pink/90 text-white rounded-full px-10 py-6 text-lg font-bold shadow-2xl shadow-wedding-pink/40 transform hover:scale-105 transition-all duration-300 group">
                  <Link to="/register">
                    <HeartHandshake className="mr-2 h-6 w-6 group-hover:animate-pulse" />
                    免費建立邀請函
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="w-full sm:w-auto bg-white/70 backdrop-blur-sm border-2 border-wedding-pink/50 text-wedding-pink hover:bg-wedding-pink/10 hover:text-wedding-pink rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300">
                  <a href="/invite/demo101wedding" target="_blank" rel="noopener noreferrer">
                    先看看範例邀請函 <Eye className="ml-2 h-5 w-5" />
                  </a>
                </Button>
              </div>

              <div className="mt-8 text-center">
                <p className="text-gray-500 text-sm">
                  🎉 已有 <span className="text-wedding-text-title font-semibold">100+</span> 對新人選擇我們 •
                  <span className="text-wedding-text-title font-semibold">5 分鐘</span>完成設定 •
                  省下<span className="text-wedding-text-title font-semibold">好幾個</span>沒日沒夜
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <LandingFooter layout="grid" brandName={BRAND_CONFIG.name}>
        <div>
          <h4 className="font-semibold text-wedding-text-title mb-3">產品</h4>
          <nav className="flex flex-col gap-2 text-sm">
            <a href="#features" className="text-gray-600 hover:text-wedding-pink">功能亮點</a>
            <Link to="/features" className="text-gray-600 hover:text-wedding-pink">功能介紹</Link>
            <Link to="/pricing" className="text-gray-600 hover:text-wedding-pink">方案與價格</Link>
            <a href="#testimonials" className="text-gray-600 hover:text-wedding-pink">新人見證</a>
          </nav>
        </div>
        <div>
          <h4 className="font-semibold text-wedding-text-title mb-3">公司</h4>
          <nav className="flex flex-col gap-2 text-sm">
            <Link to="/about" className="text-gray-600 hover:text-wedding-pink">關於我們</Link>
            <Link to="/contact" className="text-gray-600 hover:text-wedding-pink">聯絡我們</Link>
            <Link to="/privacy" className="text-gray-600 hover:text-wedding-pink">隱私權政策</Link>
          </nav>
        </div>
        <div>
          <h4 className="font-semibold text-wedding-text-title mb-3">保持聯繫</h4>
          <p className="text-sm text-gray-600">獲取最新資訊與婚禮靈感。</p>
        </div>
      </LandingFooter>
    </div>
  );
};

export default LandingPage;
